/** Original scores and sound design. Pure PCM generation; no network or combat RNG. */
export type FlightSound = "shot" | "hit" | "hurt" | "burst" | "upgrade" | "boss" | "pickup" | "evolve" | "skill" | "straight" | "double" | "fan" | "laser" | "flame" | "rocket" | "rail" | "tesla" | "warning" | "down" | "revive" | "finish";
export type FlightMusic = "hangar" | "battle" | "boss" | "result";
export const MUSIC_TITLES: Record<FlightMusic, string> = { hangar: "纸翼启航", battle: "彩墨航线", boss: "机甲来信", result: "星光归航" };
// seconds, cooldown(ms), start/end Hz, noise, level, priority (reserve critical voices).
export const SOUND_DESIGN: Record<FlightSound, readonly number[]> = {
  shot: [.07, 110, 750, 240, .12, .13, 0],
  straight: [.065, 125, 920, 290, .14, .12, 0],
  double: [.12, 155, 640, 260, .18, .13, 0],
  fan: [.19, 280, 190, 65, .72, .19, 0],
  laser: [.19, 230, 460, 920, .025, .10, 0],
  flame: [.22, 220, 110, 60, .9, .12, 0],
  rocket: [.28, 350, 210, 55, .6, .19, 0],
  rail: [.32, 420, 1450, 75, .32, .18, 0],
  tesla: [.17, 240, 870, 220, .38, .15, 0],
  hit: [.07, 100, 520, 170, .4, .10, 1],
  burst: [.36, 130, 125, 36, .8, .23, 1],
  hurt: [.42, 450, 210, 70, .22, .23, 3],
  pickup: [.30, 180, 880, 1320, 0, .15, 2],
  upgrade: [.58, 260, 440, 880, 0, .20, 2],
  boss: [.85, 650, 110, 38, .6, .29, 3],
  evolve: [.95, 650, 330, 1320, .04, .23, 3],
  skill: [.54, 420, 400, 65, .6, .24, 2],
  warning: [.75, 1600, 165, 220, .05, .19, 3],
  down: [.55, 500, 440, 110, .08, .18, 3],
  revive: [.65, 500, 330, 990, 0, .20, 3],
  finish: [1.1, 1000, 330, 660, 0, .18, 3],
};
const TAU = Math.PI * 2;
const hz = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);
function random(seed: number) {
  let n = seed | 0;
  return () => { n ^= n << 13; n ^= n >>> 17; n ^= n << 5; return (n >>> 0) / 2147483648 - 1; };
}
export function renderFlightSound(kind: FlightSound, rate: number, variant = 0): Float32Array {
  const [duration, , pitch, end, noise, volume] = SOUND_DESIGN[kind];
  const data = new Float32Array(Math.ceil(rate * duration));
  const rng = random(513 + variant * 179), tune = 1 + (variant - 1) * .024;
  const musical = ["pickup", "upgrade", "evolve", "revive", "finish"].includes(kind);
  const intervals = kind === "finish" ? [0, 7, 12, 16] : kind === "pickup" ? [0, 7, 12] : [0, 4, 7, 12];
  let phase = 0, lowNoise = 0;
  for (let i = 0; i < data.length; i++) {
    const sec = i / rate, t = i / data.length;
    lowNoise += (rng() - lowNoise) * (kind === "flame" ? .12 : .32);
    const frequency = musical ? pitch * Math.pow(2, intervals[Math.min(intervals.length - 1, Math.floor(t * intervals.length))] / 12)
      : pitch * Math.pow(end / pitch, t);
    phase += frequency * tune / rate * TAU;
    let tonal = Math.sin(phase) * .8 + Math.sin(phase * 2) * .13;
    let envelope = Math.min(1, sec / .004) * Math.pow(1 - t, 1.8);
    if (musical) { const part = (t * intervals.length) % 1; envelope *= Math.min(1, part * 18, (1 - part) * 12) * Math.exp(-part * 2.4); }
    if (kind === "double") envelope *= .42 + .58 * Math.pow(Math.cos(t * Math.PI * 2), 8);
    if (kind === "laser") tonal = Math.sin(phase + Math.sin(sec * 48) * .5) * .7;
    if (kind === "tesla") envelope *= .45 + .55 * Math.pow(Math.sin(sec * 93), 2);
    if (kind === "warning") envelope *= Math.pow(Math.max(0, Math.sin(t * Math.PI * 6)), 2);
    if (kind === "burst" || kind === "boss" || kind === "rocket") tonal += Math.sin(sec * TAU * 52 * (1 - t * .7)) * .32;
    data[i] = Math.tanh((tonal * (1 - noise) + lowNoise * noise * 1.7) * envelope * volume);
  }
  // Explicit silent endpoints prevent source start/stop clicks.
  data[0] = data[data.length - 1] = 0;
  return data;
}
const SCORES = {
  hangar: { bpm: 96, roots: [48, 53, 55, 48, 57, 53, 55, 48], minor: false, motif: [12, 16, 19, 16, 14, 19, 16, 12], level: .8 },
  battle: { bpm: 112, roots: [45, 41, 48, 43, 45, 41, 43, 40], minor: true, motif: [12, 19, 15, 22, 19, 15, 17, 19], level: 1 },
  boss: { bpm: 136, roots: [38, 38, 34, 36, 38, 41, 36, 37], minor: true, motif: [12, 19, 12, 15, 22, 19, 18, 15], level: 1 },
  result: { bpm: 100, roots: [48, 55, 57, 53, 48, 53, 55, 48], minor: false, motif: [19, 16, 12, 7, 12, 16, 14, 12], level: .7 },
};
function* musicChunks(track: FlightMusic, rate: number): Generator<void, [Float32Array, Float32Array]> {
  const score = SCORES[track], beat = 60 / score.bpm, length = Math.round(beat * 32 * rate);
  const left = new Float32Array(length), right = new Float32Array(length);
  function* add(start: number, duration: number, frequency: number, level: number, pan: number, voice: "keys" | "bass" | "pad" | "kick" | "snare" | "hat", seed = 7): Generator<void> {
    const offset = Math.round(start * rate), size = Math.ceil(duration * rate), rng = random(seed);
    let phase = 0, filtered = 0;
    for (let i = 0; i < size; i++) {
      const sec = i / rate, t = i / size;
      phase += frequency / rate * TAU;
      let sample = 0;
      const attack = Math.min(1, sec / (voice === "pad" ? .09 : .005));
      if (voice === "keys") sample = (Math.sin(phase) + Math.sin(phase * 2) * .22 + Math.sin(phase * 3) * .06) * Math.exp(-t * 5) * attack;
      if (voice === "bass") sample = (Math.sin(phase) + Math.sin(phase * 2) * .18) * Math.exp(-t * 3.2) * attack;
      if (voice === "pad") sample = (Math.sin(phase) + Math.sin(phase * 1.003) * .35) * attack * Math.min(1, (1 - t) * 7);
      if (voice === "kick") sample = Math.sin(TAU * (46 * sec + 8 * (1 - Math.exp(-sec * 24)))) * Math.exp(-sec * 20) * attack;
      if (voice === "snare" || voice === "hat") {
        const n = rng(); filtered += (n - filtered) * .18;
        sample = (n - filtered) * Math.exp(-sec * (voice === "hat" ? 85 : 26)) * attack;
        if (voice === "snare") sample += Math.sin(sec * TAU * 185) * Math.exp(-sec * 36) * .35;
      }
      sample *= level * score.level;
      if (i > 0 && i % 4096 === 0) yield;
      const at = (offset + i) % length;
      left[at] += sample * Math.sqrt((1 - pan) / 2);
      right[at] += sample * Math.sqrt((1 + pan) / 2);
      if (voice === "keys") {
        const delay = (at + Math.round(beat * .75 * rate)) % length;
        left[delay] += sample * .10; right[delay] += sample * .15;
      }
    }
  };
  for (let bar = 0; bar < 8; bar++) {
    const root = score.roots[bar], start = bar * 4 * beat;
    for (const [j, note] of [0, score.minor ? 3 : 4, 7].entries()) { yield* add(start, beat * 4, hz(root + note + 12), .028, (j - 1) * .6, "pad"); yield; }
    for (let step = 0; step < 8; step++) {
      const at = start + step * beat / 2;
      const ending = bar === 7 && step >= 6;
      const melody = ending ? (step === 6 ? 14 : 12) : score.motif[(step + (bar >= 4 ? 2 : 0)) % 8];
      if (track !== "result" || step % 2 === 0) yield* add(at, beat * .8, hz(root + melody + 12), .10, step % 2 ? .25 : -.25, "keys");
      if (track === "boss" || step % 2 === 0) yield* add(at, beat * .47, hz(root + (step === 4 ? 7 : 0)), .14, 0, "bass");
      yield* add(at, .065, 0, step % 2 ? .015 : .024, step % 2 ? .35 : -.35, "hat", 791 + bar * 13 + step);
      if (step === 0 || step === 4 || (track === "boss" && step === 7)) yield* add(at, .20, 0, .19, 0, "kick");
      if (step === 2 || step === 6) yield* add(at, .16, 0, .065, .12, "snare", 913 + bar);
      yield;
    }
  }
  // Gentle saturation, headroom for effects, stereo width small enough for phone speakers.
  for (let i = 0; i < length; i++) {
    left[i] = Math.tanh(left[i] * 1.4) * .82; right[i] = Math.tanh(right[i] * 1.4) * .82;
    if (i > 0 && i % 4096 === 0) yield;
  }
  return [left, right];
}

/** Sync renderer for QA and exports; production yields between instruments/half-beats. */
export function renderFlightMusic(track: FlightMusic, rate = 22050): [Float32Array, Float32Array] {
  const chunks = musicChunks(track, rate);
  let chunk = chunks.next();
  while (!chunk.done) chunk = chunks.next();
  return chunk.value;
}
export async function renderFlightMusicAsync(track: FlightMusic): Promise<[Float32Array, Float32Array]> {
  const chunks = musicChunks(track, 22050);
  let chunk = chunks.next();
  while (!chunk.done) {
    await new Promise<void>((done) => setTimeout(done, 0));
    const deadline = Date.now() + 4;
    do { chunk = chunks.next(); } while (!chunk.done && Date.now() < deadline);
  }
  return chunk.value;
}
